import React from 'react';
import { useTranslation } from 'react-i18next';

function PostEditor({ formData, setFormData, editingPost, onSubmit, onCancel }) {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-[0_8px_32px_rgba(59,48,45,0.04)] border border-accent/30 mb-8">
      <h2 className="font-playfair text-3xl font-medium text-charcoal mb-6">
        {editingPost ? t('admin.editPost') : t('admin.createPost')}
      </h2>
      <form onSubmit={onSubmit} data-testid="post-editor-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-charcoal font-medium mb-2">Title (EN)</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="title-en-input"
            />
          </div>
          <div>
            <label className="block text-charcoal font-medium mb-2">Title (FR)</label>
            <input
              type="text"
              value={formData.title_fr}
              onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="title-fr-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-charcoal font-medium mb-2">Excerpt (EN)</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              required
              rows="3"
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              data-testid="excerpt-en-input"
            ></textarea>
          </div>
          <div>
            <label className="block text-charcoal font-medium mb-2">Excerpt (FR)</label>
            <textarea
              value={formData.excerpt_fr}
              onChange={(e) => setFormData({ ...formData, excerpt_fr: e.target.value })}
              required
              rows="3"
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              data-testid="excerpt-fr-input"
            ></textarea>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-charcoal font-medium mb-2">Content (EN)</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              rows="8"
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              data-testid="content-en-input"
            ></textarea>
          </div>
          <div>
            <label className="block text-charcoal font-medium mb-2">Content (FR)</label>
            <textarea
              value={formData.content_fr}
              onChange={(e) => setFormData({ ...formData, content_fr: e.target.value })}
              required
              rows="8"
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              data-testid="content-fr-input"
            ></textarea>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-charcoal font-medium mb-2">Hero Image URL</label>
            <input
              type="url"
              value={formData.hero_image}
              onChange={(e) => setFormData({ ...formData, hero_image: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="hero-image-input"
            />
          </div>
          <div>
            <label className="block text-charcoal font-medium mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="category-select"
            >
              <option value="Wellness">Wellness</option>
              <option value="Grooming Tips">Grooming Tips</option>
              <option value="Seasonal Advice">Seasonal Advice</option>
              <option value="Community">Community</option>
            </select>
          </div>
        </div>

        {/* SEO Fields */}
        <div className="border-t border-accent/30 pt-6 mt-6 mb-6">
          <h3 className="font-playfair text-xl font-medium text-charcoal mb-4">SEO Optimization</h3>
          <div className="mb-6">
            <label className="block text-charcoal font-medium mb-2">URL Slug</label>
            <div className="flex items-center gap-2">
              <span className="text-muted-text text-sm">/blog/</span>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') })}
                placeholder="auto-generated-from-title"
                className="flex-1 px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="slug-input"
              />
            </div>
            <p className="text-xs text-muted-text mt-1">Leave empty to auto-generate from title</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-charcoal font-medium mb-2">Meta Description (EN)</label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                rows="3"
                maxLength={160}
                placeholder="Brief description for search engines (max 160 chars)"
                className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                data-testid="meta-desc-en-input"
              ></textarea>
              <p className="text-xs text-muted-text mt-1">{formData.meta_description.length}/160 characters</p>
            </div>
            <div>
              <label className="block text-charcoal font-medium mb-2">Meta Description (FR)</label>
              <textarea
                value={formData.meta_description_fr}
                onChange={(e) => setFormData({ ...formData, meta_description_fr: e.target.value })}
                rows="3"
                maxLength={160}
                placeholder="Breve description pour les moteurs de recherche (max 160 car.)"
                className="w-full px-4 py-3 rounded-2xl border border-accent/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                data-testid="meta-desc-fr-input"
              ></textarea>
              <p className="text-xs text-muted-text mt-1">{formData.meta_description_fr.length}/160 characters</p>
            </div>
          </div>
        </div>

        <div className="flex space-x-4">
          <button
            type="submit"
            className="px-8 py-4 rounded-full bg-primary hover:bg-charcoal text-white transition-colors duration-300 font-medium"
            data-testid="save-post-btn"
          >
            {t('admin.save')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-8 py-4 rounded-full border border-primary text-primary hover:bg-accent/20 transition-colors duration-300 font-medium"
            data-testid="cancel-btn"
          >
            {t('admin.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PostEditor;
